/**
 * Serviço de verificação de proximidade inteligente
 * Combina verificação por raio e por área (CEP) conforme apropriado
 */

import type {
  ProximityVerificationResult,
  ProximityVerificationOptions,
  RadiusVerificationResult,
  AreaVerificationResult
} from '../types/proximity';
import type {
  Location,
  Coordinates,
  GeographicValidationResult
} from '../types/base';
import { GeographicValidator } from '../types/utils';
import type { GeocodingResult } from '../types/geocoding';
import { DEFAULT_GEOCODING_CONFIG } from '../types/config';
import { LocationUtils } from '../types/utils';
import { hybridGeocodingService, geocodeWithBrazilValidation, geocodeWithBrazilFallback } from './hybrid-geocoding';
import { isWithinRadius } from '../utils/distance';
import { getDistance } from 'geolib';
import { logger } from '../utils/logger';

export class SmartProximityService {
  private readonly config = DEFAULT_GEOCODING_CONFIG;

  /**
   * Verifica proximidade usando estratégia inteligente
   */
  async verifyProximity(
    userCoords: Coordinates,
    location: Location,
    options: ProximityVerificationOptions = {}
  ): Promise<ProximityVerificationResult> {

    logger.log(`🎯 Smart Proximity: Iniciando verificação`, {
      userCoords: `${userCoords.latitude.toFixed(6)}, ${userCoords.longitude.toFixed(6)}`,
      location: location.endereco.substring(0, 50) + (location.endereco.length > 50 ? '...' : ''),
      hasCEP: !!location.cep
    });

    // Geocodifica o location usando estratégia híbrida
    const geocodingResult = await hybridGeocodingService.geocode(location);

    // Decide método baseado na qualidade da geocodificação
    const method = this.selectVerificationMethod(geocodingResult, location, options);

    let result: ProximityVerificationResult;

    if (method === 'radius') {
      result = await this.verifyByRadius(userCoords, geocodingResult, options);
    } else {
      result = await this.verifyByArea(userCoords, geocodingResult, location, options);
    }

    logger.log(`✅ Smart Proximity: Verificação concluída`, {
      method: result.method,
      precision: result.precision,
      result: result.isWithinTarget ? 'DENTRO' : 'FORA',
      distance: result.distance
    });

    return result;
  }

  /**
   * Verifica proximidade com validação geográfica brasileira
   */
  async verifyProximityWithBrazilValidation(
    userCoords: Coordinates,
    location: Location,
    options: ProximityVerificationOptions = {}
  ): Promise<ProximityVerificationResult & { brazilValidation?: GeographicValidationResult }> {

    logger.log(`🇧🇷 Smart Proximity + Brazil Validation: Iniciando verificação`, {
      userCoords: `${userCoords.latitude.toFixed(6)}, ${userCoords.longitude.toFixed(6)}`,
      location: location.endereco.substring(0, 50) + (location.endereco.length > 50 ? '...' : ''),
      hasCEP: !!location.cep
    });

    // Valida coordenadas do usuário
    const userValidation = GeographicValidator.validateBrazilianCoordinates(userCoords);

    if (!userValidation.isWithinBrazil) {
      logger.warn('⚠️ Coordenadas do usuário fora do Brasil', {
        userCoords,
        validation: userValidation
      });
    }

    // Geocodifica com validação brasileira
    const geocodingWithValidation = await geocodeWithBrazilValidation(location);

    // Se geocodificação falhou, retorna erro
    if (!geocodingWithValidation.latitude || !geocodingWithValidation.longitude) {
      return {
        isWithinTarget: false,
        method: 'radius',
        precision: 'medium',
        message: 'Falha na geocodificação do endereço',
        distance: undefined,
        coords: {
          user: userCoords,
          target: { latitude: 0, longitude: 0 },
          reference: 'address'
        },
        geocodingResult: {
          latitude: 0,
          longitude: 0,
          formatted: 'Falha na geocodificação',
          source: 'hybrid',
          confidence: 'medium'
        },
        brazilValidation: geocodingWithValidation.brazilValidation
      };
    }

    // Cria GeocodingResult compatível para verificação de proximidade
    const geocodingResult: GeocodingResult = {
      latitude: geocodingWithValidation.latitude,
      longitude: geocodingWithValidation.longitude,
      formatted: geocodingWithValidation.formatted,
      source: geocodingWithValidation.source,
      confidence: geocodingWithValidation.confidence,
      method: geocodingWithValidation.method
    };

    // Decide método baseado na qualidade da geocodificação
    const method = this.selectVerificationMethod(geocodingResult, location, options);

    let result: ProximityVerificationResult;

    if (method === 'radius') {
      result = await this.verifyByRadius(userCoords, geocodingResult, options);
    } else {
      result = await this.verifyByArea(userCoords, geocodingResult, location, options);
    }

    // Combina validação geográfica nos logs
    const locationValidation = geocodingWithValidation.brazilValidation;
    let combinedMessage = result.message;

    if (!userValidation.isWithinBrazil || (locationValidation && !locationValidation.isWithinBrazil)) {
      combinedMessage += ' [⚠️ Coordenadas fora do território brasileiro]';
    }

    logger.log(`✅ Smart Proximity + Brazil Validation: Verificação concluída`, {
      method: result.method,
      precision: result.precision,
      result: result.isWithinTarget ? 'DENTRO' : 'FORA',
      distance: result.distance,
      userWithinBrazil: userValidation.isWithinBrazil,
      locationWithinBrazil: locationValidation?.isWithinBrazil ?? false
    });

    return {
      ...result,
      message: combinedMessage,
      brazilValidation: locationValidation
    };
  }

  /**
   * Verifica proximidade com fallback automático para CEP se endereço não for brasileiro
   */
  async verifyProximityWithBrazilFallback(
    userCoords: Coordinates,
    location: Location,
    options: ProximityVerificationOptions = {}
  ): Promise<ProximityVerificationResult & {
    brazilValidation?: GeographicValidationResult;
    usedFallback?: boolean;
    fallbackReason?: string;
    increasedTolerance?: boolean;
  }> {
    logger.log(`🇧🇷🔄 Smart Proximity + Brazil Fallback: Iniciando verificação`, {
      userCoords: `${userCoords.latitude.toFixed(6)}, ${userCoords.longitude.toFixed(6)}`,
      location: location.endereco.substring(0, 50) + (location.endereco.length > 50 ? '...' : ''),
      hasCEP: !!location.cep
    });

    // Valida coordenadas do usuário
    const userValidation = GeographicValidator.validateBrazilianCoordinates(userCoords);

    if (!userValidation.isWithinBrazil) {
      logger.warn('⚠️ Coordenadas do usuário fora do Brasil', {
        userCoords,
        validation: userValidation
      });
    }

    // Geocodifica com lógica brasileira específica
    const geocodingWithFallback = await geocodeWithBrazilFallback(location);

    // Se geocodificação falhou, retorna erro
    if (!geocodingWithFallback.latitude || !geocodingWithFallback.longitude) {
      return {
        isWithinTarget: false,
        method: 'radius',
        precision: 'medium',
        message: 'Falha na geocodificação do endereço e fallback',
        distance: undefined,
        coords: {
          user: userCoords,
          target: { latitude: 0, longitude: 0 },
          reference: 'address'
        },
        geocodingResult: {
          latitude: 0,
          longitude: 0,
          formatted: 'Falha na geocodificação',
          source: 'hybrid',
          confidence: 'medium'
        },
        brazilValidation: geocodingWithFallback.brazilValidation,
        usedFallback: geocodingWithFallback.usedFallback,
        fallbackReason: geocodingWithFallback.fallbackReason
      };
    }

    // Cria GeocodingResult compatível para verificação de proximidade
    const geocodingResult: GeocodingResult = {
      latitude: geocodingWithFallback.latitude,
      longitude: geocodingWithFallback.longitude,
      formatted: geocodingWithFallback.formatted,
      source: geocodingWithFallback.source,
      confidence: geocodingWithFallback.confidence,
      method: geocodingWithFallback.method
    };

    // AJUSTA TOLERÂNCIA se usou CEP por coordenadas fora do Brasil
    let adjustedOptions = { ...options };
    if (geocodingWithFallback.increasedTolerance) {
      // Aumenta raio em 50% quando usa CEP como fallback para coordenadas estrangeiras
      const originalRadius = options.radiusMeters || this.config.radiusVerificationMeters;
      adjustedOptions.radiusMeters = Math.round(originalRadius * 1.5);

      logger.info('🔄 Aumentando tolerância de distância para CEP brasileiro', {
        originalRadius,
        adjustedRadius: adjustedOptions.radiusMeters,
        reason: 'Endereço original fora do Brasil'
      });
    }

    // Decide método baseado na qualidade da geocodificação
    const method = this.selectVerificationMethod(geocodingResult, location, adjustedOptions);

    let result: ProximityVerificationResult;

    if (method === 'radius') {
      result = await this.verifyByRadius(userCoords, geocodingResult, adjustedOptions);
    } else {
      result = await this.verifyByArea(userCoords, geocodingResult, location, adjustedOptions);
    }

    // Combina informações sobre fallback na mensagem
    let combinedMessage = result.message;

    if (geocodingWithFallback.usedFallback) {
      combinedMessage += ` [🔄 Usado CEP como fallback: ${geocodingWithFallback.fallbackReason}]`;
    }

    if (!userValidation.isWithinBrazil || (geocodingWithFallback.brazilValidation && !geocodingWithFallback.brazilValidation.isWithinBrazil)) {
      combinedMessage += ' [⚠️ Coordenadas fora do território brasileiro]';
    }

    logger.log(`✅ Smart Proximity + Brazil Fallback: Verificação concluída`, {
      method: result.method,
      precision: result.precision,
      result: result.isWithinTarget ? 'DENTRO' : 'FORA',
      distance: result.distance,
      userWithinBrazil: userValidation.isWithinBrazil,
      locationWithinBrazil: geocodingWithFallback.brazilValidation?.isWithinBrazil ?? false,
      usedFallback: geocodingWithFallback.usedFallback,
      fallbackReason: geocodingWithFallback.fallbackReason
    });

    return {
      ...result,
      message: combinedMessage,
      brazilValidation: geocodingWithFallback.brazilValidation,
      usedFallback: geocodingWithFallback.usedFallback,
      fallbackReason: geocodingWithFallback.fallbackReason,
      increasedTolerance: geocodingWithFallback.increasedTolerance
    };
  }

  /**
   * Verificação por raio (endereço específico)
   */
  private async verifyByRadius(
    userCoords: Coordinates,
    geocodingResult: GeocodingResult,
    options: ProximityVerificationOptions
  ): Promise<ProximityVerificationResult> {

    const radiusMeters = options.radiusMeters || this.config.radiusVerificationMeters;
    const targetCoords = {
      latitude: geocodingResult.latitude,
      longitude: geocodingResult.longitude
    };

    const isWithin = isWithinRadius(userCoords, targetCoords, radiusMeters);
    const distance = getDistance(userCoords, targetCoords);

    const message = isWithin
      ? `Dentro do raio de ${radiusMeters}m (${distance}m de distância)`
      : `Fora do raio de ${radiusMeters}m (${distance}m de distância)`;

    return {
      isWithinTarget: isWithin,
      method: 'radius',
      precision: 'high',
      distance: distance,
      message,
      coords: {
        user: userCoords,
        target: targetCoords,
        reference: geocodingResult.method === 'cep' ? 'cep' : 'address'
      },
      geocodingResult
    };
  }

  /**
   * Verificação por área (região do CEP)
   */
  private async verifyByArea(
    userCoords: Coordinates,
    geocodingResult: GeocodingResult,
    location: Location,
    _options: ProximityVerificationOptions
  ): Promise<ProximityVerificationResult> {

    const cepCenterCoords = {
      latitude: geocodingResult.latitude,
      longitude: geocodingResult.longitude
    };

    const distance = getDistance(userCoords, cepCenterCoords);

    // Estima área do CEP (aproximação baseada em densidade urbana)
    const estimatedCEPRadius = this.estimateCEPRadius(location.cep || '');
    const isWithin = distance <= estimatedCEPRadius;

    const message = isWithin
      ? `Dentro da área do CEP (${distance}m do centro)`
      : `Fora da área do CEP (${distance}m do centro, limite ~${estimatedCEPRadius}m)`;

    return {
      isWithinTarget: isWithin,
      method: 'area',
      precision: 'medium',
      distance: distance,
      message,
      coords: {
        user: userCoords,
        target: cepCenterCoords,
        reference: 'cep'
      },
      geocodingResult
    };
  }

  /**
   * Seleciona método de verificação baseado na qualidade dos dados
   */
  private selectVerificationMethod(
    geocodingResult: GeocodingResult,
    location: Location,
    options: ProximityVerificationOptions
  ): 'radius' | 'area' {

    // Se método foi especificado explicitamente
    if (options.method && options.method !== 'auto') {
      return options.method;
    }

    // Se geocodificação foi por endereço específico com alta confiança
    if (geocodingResult.method === 'address' && geocodingResult.confidence === 'high') {
      return 'radius';
    }

    // Se geocodificação foi só por CEP
    if (geocodingResult.method === 'cep') {
      return 'area';
    }

    // Se há inconsistência nos dados (híbrido com confiança média)
    if (geocodingResult.source === 'hybrid' && geocodingResult.confidence === 'medium') {
      return 'area'; // CEP é mais conservador
    }

    // Se não tem CEP, só pode usar raio
    if (!location.cep && !LocationUtils.extractCEP(location.endereco)) {
      return 'radius';
    }

    // Padrão: raio para endereços específicos
    return 'radius';
  }

  /**
   * Estima raio da área de um CEP baseado no padrão brasileiro
   */
  private estimateCEPRadius(cep: string): number {
    if (!cep || cep.length < 8) {
      return 1000; // 1km padrão se CEP inválido
    }

    const cleanCEP = cep.replace(/\D/g, '');
    const firstDigit = parseInt(cleanCEP[0]);
    const secondDigit = parseInt(cleanCEP[1]);

    // Estimativas baseadas na densidade populacional por região
    // São Paulo (01-05, 08-09): alta densidade
    if ([0, 1, 2, 3, 4, 8, 9].includes(firstDigit)) {
      return 500; // 500m para grandes centros urbanos
    }

    // Rio de Janeiro (20-23): alta densidade
    if (firstDigit === 2 && [0, 1, 2, 3].includes(secondDigit)) {
      return 600; // 600m
    }

    // Outras capitais e regiões metropolitanas
    if ([1, 2, 3, 4, 5, 6, 7, 8, 9].includes(firstDigit)) {
      return 800; // 800m para cidades médias
    }

    // Regiões rurais e cidades pequenas
    return 1500; // 1.5km para áreas rurais
  }

  /**
   * Verifica múltiplas locations de uma vez
   */
  async verifyMultipleLocations(
    userCoords: Coordinates,
    locations: Location[],
    options: ProximityVerificationOptions = {}
  ): Promise<ProximityVerificationResult[]> {

    logger.log(`📍 Smart Proximity: Verificando ${locations.length} locations`);

    const results = await Promise.allSettled(
      locations.map(location => this.verifyProximity(userCoords, location, options))
    );

    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        logger.error(`❌ Erro ao verificar location ${index}:`, result.reason);
        // Retorna resultado de erro
        return {
          isWithinTarget: false,
          method: 'radius',
          precision: 'medium',
          message: `Erro na verificação: ${result.reason.message || 'Erro desconhecido'}`,
          coords: {
            user: userCoords,
            target: { latitude: 0, longitude: 0 },
            reference: 'address'
          },
          geocodingResult: {
            latitude: 0,
            longitude: 0,
            formatted: locations[index].endereco,
            source: 'opencage',
            confidence: 'low'
          }
        } as ProximityVerificationResult;
      }
    });
  }

  /**
   * Análise detalhada de proximidade (para debugging/relatórios)
   */
  async analyzeProximity(
    userCoords: Coordinates,
    location: Location
  ): Promise<{
    radiusResult: RadiusVerificationResult;
    areaResult: AreaVerificationResult;
    recommendation: 'radius' | 'area';
    geocodingAnalysis: GeocodingResult;
  }> {

    const geocodingResult = await hybridGeocodingService.geocode(location);
    const targetCoords = {
      latitude: geocodingResult.latitude,
      longitude: geocodingResult.longitude
    };

    // Testa ambos os métodos
    const distance = getDistance(userCoords, targetCoords);
    const radiusLimit = this.config.radiusVerificationMeters;
    const areaLimit = this.estimateCEPRadius(location.cep || '');

    const radiusResult: RadiusVerificationResult = {
      isWithinRadius: distance <= radiusLimit,
      distance,
      radiusMeters: radiusLimit,
      userCoords,
      targetCoords
    };

    const areaResult: AreaVerificationResult = {
      isWithinArea: distance <= areaLimit,
      estimatedAreaKm2: Math.PI * Math.pow(areaLimit / 1000, 2), // Área em km²
      centerDistance: distance,
      userCoords,
      cepCenterCoords: targetCoords
    };

    const recommendation = this.selectVerificationMethod(geocodingResult, location, {});

    return {
      radiusResult,
      areaResult,
      recommendation,
      geocodingAnalysis: geocodingResult
    };
  }
}

// Instância singleton
export const smartProximityService = new SmartProximityService();

// Função de conveniência
export async function verifySmartProximity(
  userCoords: Coordinates,
  location: Location,
  options?: ProximityVerificationOptions
): Promise<ProximityVerificationResult> {
  return await smartProximityService.verifyProximity(userCoords, location, options);
}

// Função de conveniência com validação brasileira
export async function verifySmartProximityWithBrazilValidation(
  userCoords: Coordinates,
  location: Location,
  options?: ProximityVerificationOptions
): Promise<ProximityVerificationResult & { brazilValidation?: GeographicValidationResult }> {
  return await smartProximityService.verifyProximityWithBrazilValidation(userCoords, location, options);
}

// Função de conveniência com fallback automático para CEP
export async function verifySmartProximityWithBrazilFallback(
  userCoords: Coordinates,
  location: Location,
  options?: ProximityVerificationOptions
): Promise<ProximityVerificationResult & {
  brazilValidation?: GeographicValidationResult;
  usedFallback?: boolean;
  fallbackReason?: string;
  increasedTolerance?: boolean;
}> {
  return await smartProximityService.verifyProximityWithBrazilFallback(userCoords, location, options);
}